/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { phraseListStoreToPhraseListSwagger, phraseListSwaggerToPhraseListStore } from 'src/luis/modules/features/services/mappers';
import { PhraseList } from 'src/luis/modules/features/types/PhraseList';
import { PhraseListSwagger } from 'src/luis/modules/features/types/PhraseListSwagger';
import { LuisId } from 'src/luis/types/Resource';
import { PhraseListStore } from 'src/luis/types/stores/features/PhraseListStore';

/**
 * Represents a phrase list service.
 */
export const getPhraseListService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({
        apiVersion: 'v3.0-preview'
    });
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Gets the url to use for requests.
     *
     * @param phraseListId An optional id of the phrase list to
     * make the call for.
     */
    const getRequestUrl = (phraseListId: LuisId = ''): string =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('phraseLists', `${phraseListId}`)
            .build();

    /**
     * Gets the list of phrase lists.
     *
     * @returns A list of phrase lists.
     */
    const getPhraseLists = async (): Promise<PhraseListStore[]> => {
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('phraseLists')
            .queryParam('take', '500')
            .build();
        const { data: phraseLists } = await httpService.get<PhraseListSwagger[]>(url, apiOptions);

        return phraseLists.map(phraseListSwaggerToPhraseListStore);
    };

    /**
     * Gets the phrase list by id.
     *
     * @param id The id of the phrase list to get
     */
    const getPhraseListById = async ({ id }: Pick<PhraseListStore, 'id'>): Promise<PhraseListStore> => {
        const { data: phraseList } = await httpService.get<PhraseListSwagger>(getRequestUrl(id), apiOptions);

        return phraseListSwaggerToPhraseListStore(phraseList);
    };

    /**
     * Creates a new phrase list.
     *
     * @param param0.name The phrase list name.
     * @param param0.phrases The phrase list phrases.
     * @param param0.isExchangeable Whether the phrase list is exchangeable or not.
     * @returns The new phrase list created.
     */
    const createPhraseList = async (phraseList: PhraseList): Promise<PhraseListStore> => {
        const { data: phraseListId } = await httpService.post<string>(
            getRequestUrl(),
            phraseListStoreToPhraseListSwagger(phraseList),
            apiOptions
        );

        return await getPhraseListById({ id: phraseListId });
    };

    /**
     * Updates the given phrase list.
     *
     * @param phraseList The phrase list to update.
     */
    const updatePhraseList = async (phraseList: PhraseListSwagger): Promise<void> => {
        await httpService.put(getRequestUrl(phraseList.id), phraseList, apiOptions);
    };

    /**
     * Deletes the given phrase list with the given id.
     *
     * @param param0.id The id of phrase list to delete.
     */
    const deletePhraseList = async ({ id }: Pick<PhraseListStore, 'id'>): Promise<void> => {
        await httpService.delete(getRequestUrl(id), apiOptions);
    };

    return { getPhraseLists, getPhraseListById, createPhraseList, updatePhraseList, deletePhraseList };
});
